import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Center } from "@/common/css";
import { RxCross2 } from "react-icons/rx";
import { FiSearch } from "react-icons/fi";
import { useDebounceSearch } from "@/hooks/useDebounceSearch";
import { useGlobalSearch } from "@/services/employee.services";
import ProfileIcon from "../header/ProfileIcon";
import useClickOutside from "@/hooks/useClickOutSide";

export interface SearchResult {
  empExtId: string;
  empId: string;
  fullName: string;
  iconText: string;
  iconUrl?: string;
}

export interface SearchResponse {
  results: SearchResult[];
  pagination: {
    page: number;
    perPage: number;
    total: number;
  };
}

const DashboardSearch: React.FC = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);

  const searchRef = useClickOutside(() => {
    setShowResults(false);
    setSearchInput("");
  });
  const debouncedQuery = useDebounceSearch(searchInput, 300);

  const { data, isLoading, isFetching, refetch }: any = useGlobalSearch({
    q: debouncedQuery,
  });
  const isFetchingResult = isLoading || isFetching;

  const handleSearch = (value: string) => {
    setSearchInput(value);
    if (value.trim()) {
      setShowResults(true);
    } else {
      setShowResults(false);
    }
  };

  const handleClearSearch = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    setSearchInput("");
    setShowResults(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setSearchInput("");
      setShowResults(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    navigate(`/home/employees/${result.empExtId}/overview`);
    setShowResults(false);
    setSearchInput("");
    setIsMobileModalOpen(false);
  };

  const handleMobileSearchClick = () => {
    setIsMobileModalOpen(true);
  };

  const closeMobileModal = () => {
    setIsMobileModalOpen(false);
    setShowResults(false);
  };

  const handleMobileKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      closeMobileModal();
      setSearchInput("");
    }
  };

  useEffect(() => {
    if (debouncedQuery) {
      refetch();
    }
  }, [debouncedQuery, refetch]);

  return (
    <>
      {/* Desktop Search */}
      <div ref={searchRef} className="relative ml-12 md:m-1 hidden md:block">
        <input
          type="text"
          placeholder="Search employees..."
          value={searchInput}
          onChange={(e) => {
            const value = e.target.value;
            // Block '%' and length > 50
            if (value.includes("%") || value.length > 50) return;
            // Block initial space
            if (value.length > 0 && value.trim().length === 0) return;
            handleSearch(value);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (searchInput.trim() && data?.results?.length) {
              setShowResults(true);
            }
          }}
          className="pl-3 pr-[54px] py-1.5 w-32 md:w-60 2xl:w-80 rounded-md border border-gray-300 text-13 !leading-normal outline-none focus:ring-1 focus:ring-text-primary placeholder:text-gray-400 transition-all duration-300 ease-in-out"
        />

        <Center className="absolute right-3 top-1/2 -translate-y-1/2">
          {searchInput?.length > 0 ? (
            <RxCross2 className="size-5 text-text-default hover:cursor-pointer hover:text-text-primary transition-colors" onClick={handleClearSearch} />
          ) : (
            <FiSearch className="text-text-primary" />
          )}
        </Center>

        {/* Desktop Search Results Dropdown */}
        {showResults && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto animate-in fade-in slide-in-from-top-2 duration-200">
            {isFetchingResult && <div className="p-4 text-center text-text-light2 text-13 animate-pulse">Searching...</div>}

            {!isFetchingResult && data?.results?.length === 0 && debouncedQuery && (
              <div className="p-4 text-center text-text-light2 text-13 animate-in fade-in duration-300">No employees found for "{debouncedQuery}"</div>
            )}

            {!isFetchingResult && data?.results && data.results.length > 0 && (
              <>
                <div className="px-3 py-2 border-b border-gray-100 text-xs text-text-light2 bg-gray-50 animate-in fade-in duration-200">
                  Found {data.pagination.total} employee{data.pagination.total !== 1 ? "s" : ""}
                </div>

                {data.results.map((result: SearchResult, index: number) => (
                  <div
                    key={result.empExtId}
                    onClick={() => handleResultClick(result)}
                    className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0 transition-all duration-200 ease-in-out hover:scale-[1.02] animate-in fade-in slide-in-from-left-1"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    {result.iconUrl ? (
                      <img src={result.iconUrl} alt={result.fullName} className={`rounded-full object-cover w-8 h-8 `} />
                    ) : (
                      <ProfileIcon
                        name={result.fullName}
                        hideName
                        className="bg-primaryOpacity-10 w-8 h-8 text-xs transition-transform duration-200 hover:scale-105"
                      />
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-13 text-text-default truncate transition-colors duration-200">{result.fullName}</div>
                      <div className="text-xs text-text-light2 transition-colors duration-200">EmpID: {result.empId}</div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {/* Mobile Search Button */}
      <div className="md:hidden ml-12">
        <button
          onClick={handleMobileSearchClick}
          className="flex items-center gap-2 pl-3 pr-4 py-1.5 w-32 rounded-md border border-gray-300 text-13 text-gray-400 transition-all duration-200 hover:border-text-primary"
        >
          <FiSearch className="text-text-primary" />
          <span>Search...</span>
        </button>
      </div>

      {/* Mobile Full-Screen Modal */}
      {isMobileModalOpen && (
        <div className="fixed inset-0 z-50 bg-white animate-in fade-in slide-in-from-bottom-4 duration-300 md:hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-2 px-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-text-primary pl-8">Search Employees</h2>
            <button onClick={closeMobileModal} className="p-2 rounded-full hover:bg-gray-100 transition-colors">
              <RxCross2 className="size-6 text-text-default" />
            </button>
          </div>

          {/* Search Input */}
          <div className="p-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search employees..."
                value={searchInput}
                onChange={(e) => handleSearch(e.target.value)}
                onKeyDown={handleMobileKeyDown}
                autoFocus
                className="w-full pl-4 pr-12 py-3 rounded-lg border border-gray-300 text-16 outline-none focus:ring-2 focus:ring-text-primary placeholder:text-gray-400 transition-all duration-200"
              />
              <Center className="absolute right-4 top-1/2 -translate-y-1/2">
                {searchInput?.length > 0 ? (
                  <RxCross2 className="size-6 text-text-default hover:cursor-pointer hover:text-text-primary transition-colors" onClick={handleClearSearch} />
                ) : (
                  <FiSearch className="text-text-primary size-6" />
                )}
              </Center>
            </div>
          </div>

          {/* Mobile Search Results */}
          <div className="flex-1 overflow-y-auto">
            {isFetchingResult && <div className="p-4 text-center text-text-light2 text-16 animate-pulse">Searching...</div>}

            {!isFetchingResult && data?.results?.length === 0 && debouncedQuery && (
              <div className="p-4 text-center text-text-light2 text-16 animate-in fade-in duration-300">No employees found for "{debouncedQuery}"</div>
            )}

            {!isFetchingResult && data?.results && data.results.length > 0 && (
              <div className="p-4 max-h-[calc(100dvh-200px)]">
                <div className="mb-4 text-sm text-text-light2">
                  Found {data.pagination.total} employee{data.pagination.total !== 1 ? "s" : ""}
                </div>

                {data.results.map((result: SearchResult, index: number) => (
                  <div
                    key={result.empExtId}
                    onClick={() => handleResultClick(result)}
                    className="flex items-center gap-4 p-4 mb-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-all duration-200 ease-in-out animate-in fade-in slide-in-from-bottom-2"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <ProfileIcon name={result.fullName} hideName className="bg-primaryOpacity-10 w-12 h-12 text-sm" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-16 text-text-default truncate">{result.fullName}</div>
                      <div className="text-sm text-text-light2 mt-1">EmpID: {result.empId}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardSearch;
